import React, { useState, useEffect, useRef, useCallback } from 'react'
import apiService from '../utils/api'
import { findProfanity } from '../utils/profanityDictionary'

const ROLES = [
  { value: 'visitor', label: 'Посетитель' },
  { value: 'student', label: 'Студент' },
  { value: 'teacher', label: 'Преподаватель' },
]

const STATUS_LABELS = {
  open: 'Открыто',
  in_progress: 'В работе',
  resolved: 'Решено',
  closed: 'Закрыто',
}

const STATUS_COLORS = {
  open: 'var(--accent)',
  in_progress: '#F59E0B',
  resolved: '#10B981',
  closed: 'var(--text-tertiary)',
}

const ROLE_LABELS = {
  visitor: 'Посетитель',
  student: 'Студент',
  teacher: 'Преподаватель',
}

const LAST_AUTHOR_LABELS = {
  user: 'Пользователь',
  agent: 'Администрация',
}

function formatTime(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function StartView({ onCreate, onFind }) {
  return (
    <div className="uw-view uw-view--start">
      <div className="uw-start-card">
        <div className="uw-start-card__icon"><ion-icon name="chatbox-ellipses-outline" /></div>
        <h2 className="uw-start-card__title">Единое окно</h2>
        <p className="uw-start-card__text">Создайте новое обращение или найдите уже открытые по email.</p>
        <div className="uw-start-card__actions">
          <button className="uw-submit-btn" onClick={onCreate}>
            <ion-icon name="add-circle-outline" />
            Создать обращение
          </button>
          <button className="uw-secondary-btn" onClick={onFind}>
            <ion-icon name="search-outline" />
            Найти обращения по email
          </button>
        </div>
      </div>
    </div>
  )
}

// ───────────────────────────── Экран нового обращения ─────────────────────────────
function NewTicketView({ onSuccess, onCancel }) {
  const [form, setForm] = useState({ role: 'student', name: '', email: '', subject: '', message: '' })
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')

  const submit = async (e) => {
    e.preventDefault()
    setError('')

    const profanityChecks = [
      { label: 'Имя', value: form.name },
      { label: 'Email', value: form.email },
      { label: 'Тема', value: form.subject },
      { label: 'Сообщение', value: form.message },
    ]

    const affectedFields = profanityChecks
      .filter(item => findProfanity(item.value).hasProfanity)
      .map(item => item.label)

    if (affectedFields.length > 0) {
      setError(`Обнаружены недопустимые выражения в полях: ${affectedFields.join(', ')}. Отредактируйте затронутые поля.`)
      return
    }

    const safeEmail = String(form.email ?? '').trim().toLowerCase()
    if (!safeEmail) {
      setError('Укажите email: он нужен для последующего поиска обращения.')
      return
    }

    setSending(true)

    const resp = await apiService.createUnifiedWindowTicket({
      role: form.role,
      requester_role: form.role,
      subject: form.subject,
      contact_email: safeEmail,
      contact_name: form.name || undefined,
      message: form.message,
    })
    setSending(false)

    if (!resp?.data || resp?.source === 'stale_cache') {
      setError('Сервер недоступен. Попробуйте позднее.')
      return
    }
    if (resp.data?.error) {
      setError(resp.data.error)
      return
    }

    onSuccess(resp.data.access_token, form.subject, form.name, form.email)
  }

  return (
    <div className="uw-view uw-view--new">
      <div className="uw-view-header">
        <button className="uw-back-btn" onClick={onCancel}>
          <ion-icon name="arrow-back-outline" />
        </button>
        <span className="uw-view-title">Новое обращение</span>
      </div>

      <form className="uw-new-form" onSubmit={submit}>
        <div className="uw-field">
          <label className="uw-field__label">Кто обращается</label>
          <select className="uw-select" value={form.role} onChange={e => setForm(v => ({ ...v, role: e.target.value }))}>
            {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
        </div>

        <div className="uw-field">
          <label className="uw-field__label">Имя</label>
          <input className="uw-input" value={form.name} onChange={e => setForm(v => ({ ...v, name: e.target.value }))} placeholder="Необязательно" />
        </div>

        <div className="uw-field">
          <label className="uw-field__label">Email <span className="uw-required">*</span></label>
          <input className="uw-input" type="email" required value={form.email} onChange={e => setForm(v => ({ ...v, email: e.target.value }))} placeholder="Нужен для поиска обращения" />
        </div>

        <div className="uw-field">
          <label className="uw-field__label">Тема <span className="uw-required">*</span></label>
          <input className="uw-input" required value={form.subject} onChange={e => setForm(v => ({ ...v, subject: e.target.value }))} placeholder="Кратко опишите вопрос" />
        </div>

        <div className="uw-field">
          <label className="uw-field__label">Сообщение <span className="uw-required">*</span></label>
          <textarea className="uw-textarea" required rows={5} value={form.message} onChange={e => setForm(v => ({ ...v, message: e.target.value }))} placeholder="Подробно опишите ситуацию" />
        </div>

        {error && (
          <div className="uw-alert uw-alert--error">
            <ion-icon name="alert-circle-outline" />
            {error}
          </div>
        )}

        <button className="uw-submit-btn" type="submit" disabled={sending}>
          {sending ? <span className="uw-spinner" /> : <ion-icon name="send-outline" />}
          {sending ? 'Отправка...' : 'Отправить обращение'}
        </button>
      </form>
    </div>
  )
}

// ───────────────────────────── Экран чата ─────────────────────────────
function ChatView({ ticket: savedTicket, onBack, onDeleteTicket, deleting }) {
  const [ticket, setTicket] = useState(null)
  const [messages, setMessages] = useState([])
  const [statusHistory, setStatusHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [replyText, setReplyText] = useState('')
  const [closeReason, setCloseReason] = useState('')
  const [showCloseForm, setShowCloseForm] = useState(false)
  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')
  const messagesEndRef = useRef(null)

  const load = useCallback(async () => {
    const [ticketResp, msgsResp] = await Promise.all([
      apiService.getUnifiedWindowTicket(savedTicket.token),
      apiService.getUnifiedWindowMessages(savedTicket.token),
    ])
    setLoading(false)
    if (ticketResp?.data && !ticketResp.data.error) {
      setTicket(ticketResp.data)
      setStatusHistory(ticketResp.data.status_history || [])
    }
    if (Array.isArray(msgsResp?.data)) setMessages(msgsResp.data)
  }, [savedTicket.token])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  const sendReply = async (e) => {
    e.preventDefault()
    if (!replyText.trim()) return

    if (findProfanity(replyText).hasProfanity) {
      setError('В сообщении обнаружены недопустимые выражения. Отредактируйте сообщение и используйте более корректные формулировки.')
      return
    }
    
    // Запретить отправку если обращение закрыто или решено
    if (ticket && (ticket.status === 'closed' || ticket.status === 'resolved')) {
      setError('Это обращение закрыто или решено. Отправка сообщений невозможна.')
      return
    }
    
    setError('')
    setSending(true)

    const resp = await apiService.replyUnifiedWindowTicket(savedTicket.token, replyText.trim(), savedTicket.contact_name)
    setSending(false)

    if (!resp?.data || resp.data?.error) {
      setError(resp?.data?.error || 'Не удалось отправить')
      return
    }

    setReplyText('')
    load()
  }

  const closeTicket = async () => {
    const safeReason = closeReason.trim()
    if (!safeReason) {
      setError('Чтобы закрыть обращение, укажите причину.')
      return
    }

    setError('')
    setSending(true)

    const resp = await apiService.closeUnifiedWindowTicket(savedTicket.token, safeReason, savedTicket.contact_name)
    setSending(false)

    if (!resp?.data || resp.data?.error) {
      setError(resp?.data?.error || 'Не удалось закрыть обращение')
      return
    }

    setCloseReason('')
    setShowCloseForm(false)
    load()
  }

  const isClosed = ticket?.status === 'closed' || ticket?.status === 'resolved'

  return (
    <div className="uw-view uw-view--chat">
      <div className="uw-view-header uw-view-header--sticky">
        <button className="uw-back-btn" onClick={onBack}>
          <ion-icon name="arrow-back-outline" />
        </button>
        <div className="uw-chat-header-info">
          <span className="uw-view-title">{savedTicket.subject}</span>
          {ticket && (
            <span className="uw-status-badge" style={{ backgroundColor: STATUS_COLORS[ticket.status] + '22', color: STATUS_COLORS[ticket.status] }}>
              {STATUS_LABELS[ticket.status] ?? ticket.status}
            </span>
          )}
        </div>
        {!isClosed && !loading && (
          <button className="uw-secondary-btn uw-secondary-btn--sm" onClick={() => setShowCloseForm(v => !v)}>
            <ion-icon name="close-circle-outline" />
            Закрыть
          </button>
        )}
      </div>

      {loading ? (
        <div className="uw-loading"><span className="uw-spinner" /></div>
      ) : (
        <div className="uw-messages">
          {statusHistory && statusHistory.length > 0 && (
            <div className="uw-status-history">
              <div className="uw-status-history__title">История статусов</div>
              {statusHistory.map((item, i) => (
                <div key={i} className="uw-status-history__item">
                  <span>{STATUS_LABELS[item.from_status] ?? item.from_status}</span>
                  <span>→</span>
                  <span>{STATUS_LABELS[item.to_status] ?? item.to_status}</span>
                  {item.comment && <span className="uw-status-history__comment">({item.comment})</span>}
                  <span>{formatTime(item.created_at)}</span>
                </div>
              ))}
            </div>
          )}

          {messages.map(m => (
            <div key={m.id} className={`uw-message uw-message--${m.author_role === 'user' ? 'user' : 'agent'}`}>
              <div className="uw-message__bubble">
                <span className="uw-message__text">{m.text}</span>
                <span className="uw-message__time">{formatTime(m.created_at)}</span>
              </div>
              {m.author_role !== 'user' && m.author_name && (
                <span className="uw-message__author">{m.author_name}</span>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      )}

      <div className="uw-reply-form uw-reply-form--sticky">
        {error && <div className="uw-alert uw-alert--error">{error}</div>}

        {showCloseForm && !isClosed && (
          <div className="uw-close-form">
            <label className="uw-field__label">Причина закрытия <span className="uw-required">*</span></label>
            <textarea
              className="uw-reply-input"
              rows={2}
              value={closeReason}
              onChange={e => setCloseReason(e.target.value)}
              placeholder="Опишите, почему закрываете обращение"
            />
            <button className="uw-submit-btn" type="button" onClick={closeTicket} disabled={sending || !closeReason.trim()}>
              {sending ? <span className="uw-spinner" /> : <ion-icon name="checkmark-outline" />}
              Подтвердить закрытие
            </button>
          </div>
        )}

        {!isClosed && !loading && (
          <form className="uw-reply-row" onSubmit={sendReply}>
            <textarea
              className="uw-reply-input"
              value={replyText}
              onChange={e => setReplyText(e.target.value)}
              placeholder="Написать сообщение..."
              rows={2}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(e) } }}
            />
            <button className="uw-reply-send" type="submit" disabled={sending || !replyText.trim()}>
              {sending ? <span className="uw-spinner uw-spinner--sm" /> : <ion-icon name="send" />}
            </button>
          </form>
        )}
      </div>

      {isClosed && !loading && (
        <div className="uw-closed-notice">
          <div>
            {ticket?.status === 'closed' ? 'Обращение закрыто. Чтобы открыть новое, вернитесь к выбору действия.' : 'Обращение решено. Отправка сообщений недоступна.'}
          </div>
          <button
            type="button"
            className="uw-secondary-btn uw-secondary-btn--sm"
            onClick={() => onDeleteTicket({
              id: ticket?.id ?? savedTicket?.id,
              token: savedTicket?.token,
              status: ticket?.status,
            })}
            disabled={deleting || !savedTicket?.token}
          >
            {deleting ? <span className="uw-spinner uw-spinner--sm" /> : <ion-icon name="trash-outline" />}
            Удалить обращение
          </button>
        </div>
      )}
    </div>
  )
}

// ───────────────────────────── Успешная отправка ─────────────────────────────
function SuccessView({ token, onViewTicket, onCreateNew, onBackToList, hasEmail }) {
  return (
    <div className="uw-view uw-view--success">
      <div className="uw-success-icon">
        <ion-icon name="checkmark-circle" />
      </div>
      <div className="uw-success-title">Обращение отправлено!</div>
      <div className="uw-success-sub">Мы ответим вам в ближайшее время.</div>
      <div className="uw-success-token">
        <span>Ваш токен для отслеживания:</span>
        <code>{token}</code>
      </div>
      <div className="uw-success-actions">
        <button className="uw-submit-btn" onClick={onViewTicket}>
          <ion-icon name="chatbubbles-outline" />
          Открыть чат
        </button>
        {hasEmail && (
          <button className="uw-back-link-btn" onClick={onBackToList}>Показать историю по email</button>
        )}
        <button className="uw-back-link-btn" onClick={onCreateNew}>Создать ещё одно</button>
      </div>
    </div>
  )
}

// ───────────────────────────── Список обращений ─────────────────────────────
function TicketListView({
  tickets,
  lookupEmail,
  onLookupEmailChange,
  onLoadTickets,
  loadingTickets,
  error,
  hasSearched,
  onSelect,
  onBack,
}) {
  const onSubmit = (e) => {
    e.preventDefault()
    onLoadTickets()
  }

  return (
    <div className="uw-view uw-view--list">
      <div className="uw-view-header">
        <button className="uw-back-btn" onClick={onBack}>
          <ion-icon name="arrow-back-outline" />
        </button>
        <span className="uw-view-title">Поиск обращений</span>
      </div>

      <form className="uw-new-form" onSubmit={onSubmit}>
        <div className="uw-field">
          <label className="uw-field__label">Email для поиска обращений</label>
          <input
            className="uw-input"
            type="email"
            value={lookupEmail}
            onChange={e => onLookupEmailChange(e.target.value)}
            placeholder="Введите email, указанный при обращении"
            required
          />
        </div>
        {error && (
          <div className="uw-alert uw-alert--error">
            <ion-icon name="alert-circle-outline" />
            {error}
          </div>
        )}
        <button className="uw-submit-btn" type="submit" disabled={loadingTickets}>
          {loadingTickets ? <span className="uw-spinner" /> : <ion-icon name="search-outline" />}
          {loadingTickets ? 'Загрузка...' : 'Найти обращения'}
        </button>
      </form>

      {hasSearched && tickets.length === 0 ? (
        <div className="uw-empty">
          <ion-icon name="chatbubbles-outline" />
          <div>По указанному email обращений не найдено.</div>
        </div>
      ) : hasSearched ? (
        <div className="uw-ticket-list">
          {tickets.map(t => (
            <div
              key={t.token}
              className="uw-ticket-item"
              role="button"
              tabIndex={0}
              onClick={() => onSelect(t)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  onSelect(t)
                }
              }}
            >
              <div className="uw-ticket-item__icon">
                <ion-icon name="chatbubble-outline" />
              </div>
              <div className="uw-ticket-item__body">
                <div className="uw-ticket-item__subject-row">
                  <div className="uw-ticket-item__subject">#{t.id} • {t.subject}</div>
                  {t.has_unread_for_user ? <span className="uw-ticket-unread">Непрочитано</span> : null}
                </div>
                <div className="uw-ticket-item__meta">
                  {formatTime(t.created_at)} • {STATUS_LABELS[t.status] ?? t.status}
                </div>
                <div className="uw-ticket-item__meta">{t.contact_name || 'Без имени'} • {ROLE_LABELS[t.requester_role] || 'Пользователь'}</div>
                <div className="uw-ticket-item__meta">{t.contact_email || 'без email'} • Последнее: {LAST_AUTHOR_LABELS[t.last_message_author_role] || '—'}</div>
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  )
}

// ───────────────────────────── Корневой компонент ─────────────────────────────
export default function UnifiedWindowScreen() {
  const [tickets, setTickets] = useState([])
  const [lookupEmail, setLookupEmail] = useState('')
  const [loadingTickets, setLoadingTickets] = useState(false)
  const [listError, setListError] = useState('')
  const [hasSearched, setHasSearched] = useState(false)
  const [view, setView] = useState('start') // start | list | new | chat | success
  const [activeTicket, setActiveTicket] = useState(null)
  const [successInfo, setSuccessInfo] = useState(null) // { token, subject, name, email }
  const [deletingToken, setDeletingToken] = useState('')

  const loadByEmail = useCallback(async (email) => {
    setListError('')
    const safeEmail = String(email ?? '').trim().toLowerCase()
    if (!safeEmail) {
      setListError('Введите email для поиска')
      setTickets([])
      setHasSearched(false)
      return
    }

    setHasSearched(true)
    setLoadingTickets(true)
    const resp = await apiService.getUnifiedWindowTicketsByEmail(safeEmail)
    setLoadingTickets(false)

    if (!Array.isArray(resp?.data)) {
      setListError(resp?.data?.error || 'Не удалось получить список обращений')
      setTickets([])
      return
    }

    setTickets(resp.data.map(t => ({
      id: t.id,
      token: t.access_token,
      subject: t.subject,
      contact_name: t.contact_name,
      contact_email: t.contact_email,
      requester_role: t.requester_role,
      status: t.status,
      has_unread_for_user: Boolean(t.has_unread_for_user),
      last_message_author_role: t.last_message_author_role || null,
      created_at: t.created_at,
    })))
  }, [])

  const handleNew = () => setView('new')
  const handleCancel = () => setView('start')
  const handleFind = () => {
    setHasSearched(false)
    setListError('')
    setTickets([])
    setView('list')
  }

  const handleSuccess = (token, subject, name, email) => {
    setSuccessInfo({ token, subject, name, email })
    if (email) {
      setLookupEmail(String(email).toLowerCase())
      loadByEmail(email)
    }
    setView('success')
  }

  const handleViewTicket = () => {
    if (!successInfo?.token) return
    setActiveTicket({ token: successInfo.token, subject: successInfo.subject, contact_name: successInfo.name })
    setView('chat')
  }

  const handleSelect = (t) => { setActiveTicket(t); setView('chat') }
  const handleBackToList = () => {
    setActiveTicket(null)
    setView('list')
    if (lookupEmail) loadByEmail(lookupEmail)
  }

  const handleDeleteTicket = async (ticket) => {
    const statusLabel = ticket?.status === 'resolved' ? 'решенное' : 'закрытое'
    const confirmed = window.confirm(`Удалить ${statusLabel} обращение #${ticket.id}? Действие необратимо.`)
    if (!confirmed) return

    setDeletingToken(ticket.token)
    const resp = await apiService.deleteUnifiedWindowTicket(ticket.token)
    setDeletingToken('')

    if (resp?.data?.error) {
      setListError(resp.data.error)
      return
    }

    if (activeTicket?.token === ticket.token) {
      setActiveTicket(null)
      setView('list')
    }

    if (lookupEmail) {
      loadByEmail(lookupEmail)
    }
  }

  return (
    <section className="screen uw-screen">
      {view === 'start' && (
        <StartView
          onCreate={() => setView('new')}
          onFind={handleFind}
        />
      )}
      {view === 'list' && (
        <TicketListView
          tickets={tickets}
          lookupEmail={lookupEmail}
          onLookupEmailChange={setLookupEmail}
          onLoadTickets={() => loadByEmail(lookupEmail)}
          loadingTickets={loadingTickets}
          error={listError}
          hasSearched={hasSearched}
          onSelect={handleSelect}
          onBack={() => setView('start')}
        />
      )}
      {view === 'new' && (
        <NewTicketView
          onSuccess={handleSuccess}
          onCancel={handleCancel}
        />
      )}
      {view === 'success' && successInfo && (
        <SuccessView
          token={successInfo.token}
          onViewTicket={handleViewTicket}
          onBackToList={() => {
            setView('list')
            if (successInfo.email) loadByEmail(successInfo.email)
          }}
          hasEmail={Boolean(successInfo.email)}
          onCreateNew={() => setView('new')}
        />
      )}
      {view === 'chat' && activeTicket && (
        <ChatView
          ticket={activeTicket}
          onBack={handleBackToList}
          onDeleteTicket={handleDeleteTicket}
          deleting={deletingToken === activeTicket?.token}
        />
      )}
    </section>
  )
}
